import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  FileSearch,
  DraftingCompass,
  Layers,
  Cog,
  ShieldCheck,
  Truck,
} from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Reveal, ScaleImage } from "@/components/site/Reveal";

const STEP_ICONS = [FileSearch, DraftingCompass, Layers, Cog, ShieldCheck, Truck];

const STEPS = [
  {
    no: "01",
    title: "Enquiry & Drawing Review",
    text: "Every engagement begins with a detailed review of client drawings, specifications, and requirements — assessing feasibility, tolerances, and the standards the component must meet before any work begins.",
  },
  {
    no: "02",
    title: "Engineering & Process Planning",
    text: "Components are planned end-to-end: material selection, machining and fabrication sequencing, and the process route required to meet tolerance and quality requirements for critical applications.",
  },
  {
    no: "03",
    title: "Material Sourcing",
    text: "Materials are sourced and verified against specification, drawing on deep expertise across a wide range of raw materials and their applications — ensuring the right material for the right application, every time.",
  },
  {
    no: "04",
    title: "Precision Machining & Fabrication",
    text: "Components move through our machine shop and fabrication units — vertical machining, horizontal boring, CNC turning, and skilled fabrication with welding performed by BV Marine-qualified welders certified to the ISO 15609-1 standard.",
  },
  {
    no: "05",
    title: "Inspection & TPA Compliance",
    text: "Every component is verified through in-house inspection under our ISO 9001:2015-certified quality system, with strict adherence to inspection requirements and third-party approvals (TPA).",
  },
  {
    no: "06",
    title: "Packing & Dispatch",
    text: "Finished components are prepared and dispatched to meet the handling and delivery requirements.",
  },
];

const CARDS = [
  {
    label: "Infrastructure",
    no: "01",
    text: "VMCs, horizontal boring machines and CNC turning centres across three manufacturing units in Thane and Ambernath.",
    href: "/capabilities/infrastructure",
    image: "/assets/images/workshop/unit2_ambernath_machine_shop_interior.jpg",
  },
  {
    label: "Products",
    no: "02",
    text: "Precision-machined and heavy-fabricated equipment across marine, steel, power and defence domains.",
    href: "/capabilities/products",
    image: encodeURI("/assets/products/Marine and deck equipment/Winch_Drums.png"),
  },
];

function Step({ step, index, isLast, setActive }) {
  const ref = useRef(null);
  const active = useInView(ref, { margin: "-45% 0px -45% 0px" });

  useEffect(() => {
    if (active) setActive(index);
  }, [active, index, setActive]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative pl-16 md:pl-24 pb-10 last:pb-0"
      data-testid={`step-${step.no}`}
    >
      {/* Connector line */}
      {!isLast && (
        <span
          className={`absolute left-[15px] md:left-[23px] top-12 bottom-0 w-px transition-colors duration-500 ${
            active ? "bg-[hsl(var(--signal))]" : "bg-border"
          }`}
        />
      )}
      {/* Node */}
      <span
        className={`absolute left-0 top-1 grid place-items-center h-8 w-8 md:h-12 md:w-12 border transition-all duration-500 ${
          active
            ? "border-[hsl(var(--signal))] bg-[hsl(var(--signal))] text-white"
            : "border-border bg-white text-muted-foreground"
        }`}
      >
        <span className="font-mono-tech text-[11px] md:text-sm">{step.no}</span>
      </span>

      <div
        className={`transition-all duration-500 ${
          active ? "opacity-100 translate-x-0" : "opacity-45"
        }`}
      >
        <h3
          className={`font-display font-medium text-2xl md:text-4xl tracking-tight mb-3 transition-colors duration-500 ${
            active ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {step.title}
        </h3>
        <p className="text-muted-foreground leading-relaxed max-w-2xl md:text-lg">{step.text}</p>
      </div>
    </motion.div>
  );
}

export default function Capabilities() {
  const [activeIndex, setActiveIndex] = useState(0);
  const ActiveIcon = STEP_ICONS[activeIndex];

  return (
    <div data-testid="capabilities-page">
      <PageHero
        index="/ 03"
        overline="Capabilities"
        title={<>Engineered end to end,<br />start to dispatch.</>}
        subtitle="An integrated capability spanning process, machining, fabrication, inspection and delivery — under a single quality umbrella."
      />

      {/* HOW WE WORK */}
      <section className="py-14 md:py-24" data-testid="how-we-work">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-16 md:mb-24">
              <div className="lg:col-span-7">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                  [ How We Work ]
                </span>
                <h2 className="mt-4 font-display font-medium tracking-tight text-4xl md:text-6xl leading-[1] max-w-2xl">
                  From Drawing to Delivery
                </h2>
              </div>
              <p className="lg:col-span-5 self-end text-muted-foreground leading-relaxed">
                A structured process built around precision, traceability, and compliance at every stage.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Steps */}
            <div className="lg:col-span-7">
              {STEPS.map((s, i) => (
                <Step key={s.no} step={s} index={i} isLast={i === STEPS.length - 1} setActive={setActiveIndex} />
              ))}
            </div>

            {/* Sticky icon panel */}
            <div className="hidden lg:block lg:col-span-5">
              <div className="sticky top-32 flex items-center justify-center min-h-[60vh]">
                <div className="relative w-full max-w-[400px] aspect-square border border-border bg-secondary overflow-hidden">
                  {/* subtle grid backdrop */}
                  <div
                    className="absolute inset-0 opacity-[0.5]"
                    style={{
                      backgroundImage:
                        "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
                      backgroundSize: "48px 48px",
                    }}
                  />
                  <div className="absolute inset-0 grid place-items-center">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeIndex}
                        initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        exit={{ opacity: 0, scale: 0.7, rotate: 8 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        data-testid={`step-icon-${STEPS[activeIndex].no}`}
                      >
                        <ActiveIcon className="h-32 w-32 text-[hsl(var(--signal))]" strokeWidth={1} />
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* footer label */}
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-4 border-t border-border bg-white/70 backdrop-blur px-6 py-4">
                    <span className="font-mono-tech text-sm text-[hsl(var(--signal))]">{STEPS[activeIndex].no}</span>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={activeIndex}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.3 }}
                        className="font-display font-medium text-lg tracking-tight text-right"
                      >
                        {STEPS[activeIndex].title}
                      </motion.span>
                    </AnimatePresence>
                  </div>

                  {/* progress ticks */}
                  <div className="absolute top-6 right-6 flex flex-col gap-2">
                    {STEPS.map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 w-6 transition-colors duration-500 ${
                          i === activeIndex ? "bg-[hsl(var(--signal))]" : "bg-border"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DEEPER: EQUIPMENT & INFRASTRUCTURE */}
      <section className="py-14 md:py-20 bg-secondary border-t border-border">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              [ Go Deeper ]
            </span>
            <h2 className="mt-4 mb-14 font-display font-medium tracking-tight text-4xl md:text-5xl leading-[1.05] max-w-xl">
              Explore our capability in detail.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CARDS.map((c, i) => (
              <Reveal key={c.href} delay={i * 0.1}>
                <Link
                  to={c.href}
                  data-testid={`capability-deep-${c.label.toLowerCase()}`}
                  className="group block bg-white border border-border hover:border-foreground transition-colors"
                >
                  <div className="relative">
                    <ScaleImage src={c.image} alt={c.label} className="h-64 md:h-72" />
                  </div>
                  <div className="flex items-start justify-between p-8 md:p-9">
                    <div>
                      <p className="font-mono-tech text-sm text-[hsl(var(--signal))] mb-4">{c.no}</p>
                      <h3 className="font-display font-medium text-2xl md:text-3xl tracking-tight mb-3">{c.label}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed max-w-md">{c.text}</p>
                    </div>
                    <ArrowUpRight className="h-6 w-6 shrink-0 text-muted-foreground group-hover:text-[hsl(var(--signal))] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
